import type { DayScheduleItem } from '@customTypes/types';
import { DayOfWeek } from '@customTypes/types';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useClubProfileScheduleMutation,
  useClubProfileScheduleQuery
} from '@/shared/api/requests/club';

export const useSchedulePage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });
  const search = useSearch({ from: '/clubOwner/$id/_onboard/onboard/$step' });
  const router = useRouter();
  const schedule = useClubProfileScheduleQuery({ clubId });
  const scheduleMutate = useClubProfileScheduleMutation();
  const [editableDay, setEditableDay] = useState('');

  const [opened, { close, open }] = useDisclosure(false);

  const next = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'contract-terms'
      }
    });
  };

  const form = useForm<{
    schedule: DayScheduleItem[];
    additional: {
      startHour: string;
      closeHour: string;
      isOpen24Hours: boolean;
    };
  }>({
    initialValues: {
      schedule: [
        {
          day: DayOfWeek.Monday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: false
        },
        {
          day: DayOfWeek.Tuesday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: false
        },
        {
          day: DayOfWeek.Wednesday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: false
        },
        {
          day: DayOfWeek.Thursday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: false
        },
        {
          day: DayOfWeek.Friday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: false
        },
        {
          day: DayOfWeek.Saturday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: false
        },
        {
          day: DayOfWeek.Sunday,
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isClosed: false,
          isOpen24Hours: true
        }
      ],
      additional: {
        startHour: '00:00:00',
        closeHour: '00:00:00',
        isOpen24Hours: false
      }
    },
    transformValues: (values) => {
      const scheduleTransformed = values.schedule.map((item) => {
        if (item.closeHour.length < 8) {
          item.closeHour = `${item.closeHour}:00`;
        }
        if (item.startHour.length < 8) {
          item.startHour = `${item.startHour}:00`;
        }
        return { ...item };
      });

      return {
        ...values,
        schedule: scheduleTransformed
      };
    }
  });

  useEffect(() => {
    if (schedule.isSuccess && schedule.data.data.length) {
      form.initialize({
        schedule: schedule.data.data,
        additional: {
          startHour: '00:00:00',
          closeHour: '00:00:00',
          isOpen24Hours: false
        }
      });
    }
  }, [schedule.data?.data]);

  const onSubmit = form.onSubmit(async (value) => {
    await scheduleMutate.mutateAsync(
      {
        params: {
          data: value.schedule,
          clubId
        }
      },
      {
        onSuccess: async () => {
          notifications.show({
            ...notificationStyleConfig,
            title: formatMessage({ id: 'notifications.successDataUpdate' })
          });

          await queryClient.invalidateQueries({
            queryKey: ['ClubProfileSchedule', clubId]
          });

          next();
        }
      }
    );
  });

  const setFullWeekData = () => {
    Object.entries(form.getValues().schedule).forEach(([, value], index) => {
      form.setFieldValue(`schedule.${index}`, {
        ...value,
        ...form.getValues().additional
      });
    });
  };

  const toProfile = () => {
    navigate({ to: '/clubOwner/$id/profile' });
  };

  const queries = [schedule];

  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: {
      isLoading,
      form,
      schedule,
      editableDay,
      modal: {
        opened,
        close,
        open
      }
    },
    functions: {
      setEditableDay,
      onSubmit,
      toProfile,
      setFullWeekData
    }
  };
};
